import { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Dialog from '@mui/material/Dialog';
import CircularProgress from '@mui/material/CircularProgress';
import DownloadIcon from '@mui/icons-material/Download';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CloseIcon from '@mui/icons-material/Close';
import { formatFileSize, fileIconFor } from './FileAttachInput';
import { notify } from './Notifier';
import { getApiErrorMessage } from '../api/client';

const PREVIEWABLE_MIME = ['application/pdf', 'image/png', 'image/jpeg', 'image/webp'];
const IMAGE_MIME = ['image/png', 'image/jpeg', 'image/webp'];

export interface SavedFile {
  id: string;
  file_name: string;
  mime: string;
  size_bytes: number;
}

interface SavedFileListProps<T extends SavedFile> {
  files: T[];
  /** Tải bytes của 1 file đã lưu (có cookie xác thực) — trả về Blob. */
  fetchBlob: (file: T) => Promise<Blob>;
  /** Bỏ qua để chỉ xem (không hiện nút xoá). */
  onRemove?: (file: T) => void;
  /** Kích thước lưới thumbnail ảnh (px). Mặc định 72, giống SavedImageGrid của bug. */
  imageSize?: number;
}

/** Thumbnail vuông cho 1 file ảnh đã lưu — tự tải blob qua fetchBlob (route /raw yêu cầu đăng nhập, không dùng <img src> trực tiếp được như bug images). */
function ImageThumb<T extends SavedFile>({
  file,
  fetchBlob,
  size,
  onRemove,
  onOpen,
}: {
  file: T;
  fetchBlob: (file: T) => Promise<Blob>;
  size: number;
  onRemove?: (file: T) => void;
  onOpen: (url: string) => void;
}): JSX.Element {
  const [url, setUrl] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    let objectUrl: string | null = null;
    void fetchBlob(file).then(
      (blob) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setUrl(objectUrl);
      },
      () => {
        if (!cancelled) notify(`Không tải được ảnh "${file.file_name}"`, 'warning');
      },
    );
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [file.id]);

  return (
    <Box
      sx={{
        position: 'relative',
        width: size,
        height: size,
        borderRadius: 1,
        overflow: 'hidden',
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: 'action.hover',
      }}
    >
      {url ? (
        <Box
          component="img"
          src={url}
          alt={file.file_name}
          onClick={() => onOpen(url)}
          sx={{ width: '100%', height: '100%', objectFit: 'cover', cursor: 'zoom-in', display: 'block' }}
        />
      ) : (
        <Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <CircularProgress size={20} />
        </Box>
      )}
      {onRemove && (
        <IconButton
          aria-label={`Xoá ảnh ${file.file_name}`}
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            onRemove(file);
          }}
          sx={{
            position: 'absolute',
            top: 2,
            right: 2,
            bgcolor: 'rgba(0,0,0,0.55)',
            color: '#fff',
            p: 0.25,
            '&:hover': { bgcolor: 'rgba(0,0,0,0.75)' },
          }}
        >
          <CloseIcon sx={{ fontSize: 16 }} />
        </IconButton>
      )}
    </Box>
  );
}

/**
 * Danh sách file ĐÃ LƯU trên server: ảnh hiện dạng lưới thumbnail (giống
 * SavedImageGrid dùng cho bug), tài liệu khác (Word/Excel/PDF...) hiện dạng
 * hàng — tên, dung lượng, icon theo loại, xem trước hoặc tải xuống.
 */
export function SavedFileList<T extends SavedFile>({
  files,
  fetchBlob,
  onRemove,
  imageSize = 72,
}: SavedFileListProps<T>): JSX.Element | null {
  const [preview, setPreview] = useState<{ file: T; blobUrl: string } | null>(null);
  const [imageViewUrl, setImageViewUrl] = useState<string | null>(null);
  const [loadingId, setLoadingId] = useState<string | null>(null);

  if (files.length === 0) return null;

  const imageFiles = files.filter((f) => IMAGE_MIME.includes(f.mime));
  const otherFiles = files.filter((f) => !IMAGE_MIME.includes(f.mime));

  const handlePreview = async (file: T) => {
    setLoadingId(file.id);
    try {
      const blob = await fetchBlob(file);
      setPreview({ file, blobUrl: URL.createObjectURL(blob) });
    } catch (err) {
      notify(getApiErrorMessage(err, 'Không thể mở xem trước'), 'error');
    } finally {
      setLoadingId(null);
    }
  };

  const handleDownload = async (file: T) => {
    setLoadingId(file.id);
    try {
      const blob = await fetchBlob(file);
      const blobUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = file.file_name;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(blobUrl);
    } catch (err) {
      notify(getApiErrorMessage(err, 'Không thể tải xuống'), 'error');
    } finally {
      setLoadingId(null);
    }
  };

  const closePreview = () => {
    if (preview) URL.revokeObjectURL(preview.blobUrl);
    setPreview(null);
  };

  return (
    <>
      {imageFiles.length > 0 && (
        <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mb: otherFiles.length > 0 ? 1 : 0 }} data-testid="saved-image-grid">
          {imageFiles.map((file) => (
            <ImageThumb
              key={file.id}
              file={file}
              fetchBlob={fetchBlob}
              size={imageSize}
              onRemove={onRemove}
              onOpen={setImageViewUrl}
            />
          ))}
        </Stack>
      )}

      {otherFiles.length > 0 && (
        <Stack spacing={0.75} data-testid="saved-file-list">
          {otherFiles.map((file) => {
            const previewable = PREVIEWABLE_MIME.includes(file.mime);
            const busy = loadingId === file.id;
            return (
              <Stack
                key={file.id}
                direction="row"
                alignItems="center"
                spacing={1}
                sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, px: 1, py: 0.5 }}
              >
                {fileIconFor(file.mime)}
                <Typography variant="body2" noWrap sx={{ flexGrow: 1, minWidth: 0 }}>
                  {file.file_name}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {file.size_bytes > 0 ? formatFileSize(file.size_bytes) : '—'}
                </Typography>
                {busy ? (
                  <CircularProgress size={18} />
                ) : (
                  <>
                    {previewable && (
                      <IconButton aria-label="Xem trước" size="small" onClick={() => void handlePreview(file)}>
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                    )}
                    <IconButton aria-label="Tải xuống" size="small" onClick={() => void handleDownload(file)}>
                      <DownloadIcon fontSize="small" />
                    </IconButton>
                    {onRemove && (
                      <IconButton aria-label="Xoá file" size="small" onClick={() => onRemove(file)}>
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    )}
                  </>
                )}
              </Stack>
            );
          })}
        </Stack>
      )}

      <Dialog open={preview !== null} onClose={closePreview} maxWidth="lg" fullWidth>
        <IconButton
          aria-label="Đóng"
          onClick={closePreview}
          sx={{ position: 'absolute', right: 8, top: 8, bgcolor: 'rgba(0,0,0,0.5)', color: '#fff', zIndex: 1 }}
        >
          <CloseIcon />
        </IconButton>
        {preview && (
          <Box sx={{ height: '80vh' }}>
            {preview.file.mime === 'application/pdf' ? (
              <Box component="iframe" src={preview.blobUrl} sx={{ width: '100%', height: '100%', border: 'none' }} />
            ) : (
              <Box
                component="img"
                src={preview.blobUrl}
                alt={preview.file.file_name}
                sx={{ display: 'block', maxWidth: '100%', maxHeight: '100%', margin: '0 auto' }}
              />
            )}
          </Box>
        )}
      </Dialog>

      <Dialog open={imageViewUrl !== null} onClose={() => setImageViewUrl(null)} maxWidth="md">
        {imageViewUrl && (
          <Box sx={{ position: 'relative' }}>
            <IconButton
              aria-label="Đóng"
              onClick={() => setImageViewUrl(null)}
              sx={{ position: 'absolute', right: 8, top: 8, bgcolor: 'rgba(0,0,0,0.5)', color: '#fff' }}
            >
              <CloseIcon />
            </IconButton>
            <Box component="img" src={imageViewUrl} alt="" sx={{ display: 'block', maxWidth: '90vw', maxHeight: '85vh' }} />
          </Box>
        )}
      </Dialog>
    </>
  );
}
